/* Hoverflux interactions: scroll-snap tracks with arrows and a progress bar, tabs (panel or
   scroll mode), autoplay hero, and thumbnail galleries. No dependencies. Everything is driven by
   data attributes so each section stays plain Liquid + CSS. Re-inits on theme editor reloads. */
(() => {
  const qa = (el, s) => Array.from(el.querySelectorAll(s));

  function slideStep(track) {
    const first = track.children[0];
    if (!first) return track.clientWidth;
    const gap = parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 0;
    return first.getBoundingClientRect().width + gap;
  }

  function initTrack(root) {
    const track = root.querySelector('[data-hf-track]');
    if (!track || track.__hf) return;
    track.__hf = true;
    const prev = qa(root, '[data-hf-prev]');
    const next = qa(root, '[data-hf-next]');
    const bar = root.querySelector('[data-hf-bar] > span');
    const update = () => {
      const max = track.scrollWidth - track.clientWidth;
      const x = track.scrollLeft;
      prev.forEach((b) => (b.disabled = x <= 2));
      next.forEach((b) => (b.disabled = x >= max - 2));
      if (bar) {
        const ratio = track.scrollWidth ? track.clientWidth / track.scrollWidth : 1;
        bar.style.width = Math.max(ratio * 100, 12) + '%';
        bar.style.transform = `translateX(${max > 0 ? (x / max) * (100 / Math.max(ratio, 0.12) - 100) : 0}%)`;
      }
      root.dispatchEvent(new CustomEvent('hf:scroll', { detail: { index: Math.round(x / slideStep(track)) } }));
    };
    prev.forEach((b) => b.addEventListener('click', () => track.scrollBy({ left: -slideStep(track), behavior: 'smooth' })));
    next.forEach((b) => b.addEventListener('click', () => track.scrollBy({ left: slideStep(track), behavior: 'smooth' })));
    track.addEventListener('scroll', () => requestAnimationFrame(update), { passive: true });
    window.addEventListener('resize', update);
    update();
  }

  function initTabs(root) {
    if (root.__hfTabs) return;
    root.__hfTabs = true;
    const tabs = qa(root, '[data-hf-tab]');
    const mode = root.dataset.hfTabsMode || 'panel';
    const track = root.querySelector('[data-hf-track]');
    const select = (i, scroll) => {
      tabs.forEach((t) => t.setAttribute('aria-selected', String(+t.dataset.hfTab === i)));
      if (mode === 'panel') {
        qa(root, '[data-hf-panel]').forEach((p) => (p.hidden = +p.dataset.hfPanel !== i));
        qa(root, '[data-hf-panel]').forEach((p) => { if (!p.hidden) qa(p, '[data-hf-track-root]').forEach(initTrack); });
        window.dispatchEvent(new Event('resize'));
      } else if (scroll && track && track.children[i]) {
        track.scrollTo({ left: track.children[i].offsetLeft - track.offsetLeft, behavior: 'smooth' });
      }
      const active = tabs.find((t) => +t.dataset.hfTab === i);
      if (active && active.parentElement) active.parentElement.scrollTo({ left: active.offsetLeft - 12, behavior: 'smooth' });
    };
    tabs.forEach((t) => t.addEventListener('click', () => select(+t.dataset.hfTab, true)));
    if (mode === 'scroll') root.addEventListener('hf:scroll', (e) => {
      tabs.forEach((t) => t.setAttribute('aria-selected', String(+t.dataset.hfTab === e.detail.index)));
    });
  }

  function initHero(root) {
    if (root.__hfHero) return;
    root.__hfHero = true;
    const track = root.querySelector('[data-hf-track]');
    const dots = qa(root, '[data-hf-dot]');
    if (!track || track.children.length < 2) return;
    let i = 0, timer;
    const go = (n) => {
      i = (n + track.children.length) % track.children.length;
      track.scrollTo({ left: track.children[i].offsetLeft - track.offsetLeft, behavior: 'smooth' });
    };
    const mark = () => dots.forEach((d, k) => d.setAttribute('aria-current', String(k === i)));
    track.addEventListener('scroll', () => {
      const n = Math.round(track.scrollLeft / track.clientWidth);
      if (n !== i) { i = n; mark(); }
    }, { passive: true });
    dots.forEach((d, k) => d.addEventListener('click', () => { go(k); mark(); restart(); }));
    const restart = () => {
      clearInterval(timer);
      const ms = +root.dataset.hfAutoplay || 0;
      if (ms > 0 && !window.matchMedia('(prefers-reduced-motion: reduce)').matches) timer = setInterval(() => { go(i + 1); mark(); }, ms);
    };
    root.addEventListener('pointerenter', () => clearInterval(timer));
    root.addEventListener('pointerleave', restart);
    mark(); restart();
  }

  function initGallery(root) {
    if (root.__hfGal) return;
    root.__hfGal = true;
    const track = root.querySelector('[data-hf-track]');
    const thumbs = qa(root, '[data-hf-thumb]');
    const show = (i) => {
      if (!track.children[i]) return;
      track.scrollTo({ left: track.children[i].offsetLeft - track.offsetLeft, behavior: 'smooth' });
    };
    thumbs.forEach((t) => t.addEventListener('click', () => show(+t.dataset.hfThumb)));
    root.addEventListener('hf:scroll', (e) => thumbs.forEach((t) => t.setAttribute('aria-current', String(+t.dataset.hfThumb === e.detail.index))));
  }

  // Variant swatches: match the checked option values to a variant, then update the hidden id,
  // prices, offer card, buttons and URL, and slide the gallery to the variant's image.
  function initVariants(root) {
    if (root.__hfVar) return;
    root.__hfVar = true;
    const data = root.querySelector('[data-hf-variants]');
    if (!data) return;
    const variants = JSON.parse(data.textContent);
    const section = root.closest('.hf-pm') || document;
    const set = (sel, fn) => qa(section, sel).forEach(fn);
    const update = () => {
      const picked = qa(root, '[data-hf-opt]:checked').sort((a, b) => a.dataset.hfOpt - b.dataset.hfOpt).map((i) => i.value);
      qa(root, '[data-hf-opt]:checked').forEach((i) => set(`[data-hf-opt-label="${i.dataset.hfOpt}"]`, (el) => (el.textContent = i.value)));
      const v = variants.find((x) => x.options.every((o, k) => o === picked[k]));
      const cart = section.querySelector('[data-hf-cart]');
      if (!v) {
        if (cart) { cart.disabled = true; cart.textContent = cart.dataset.sold; }
        set('[data-hf-buy]', (b) => (b.disabled = true));
        return;
      }
      set('[data-hf-variant-id]', (i) => (i.value = v.id));
      set('[data-hf-price]', (el) => (el.textContent = v.price));
      set('[data-hf-compare]', (el) => { el.textContent = v.compare; el.hidden = !v.pct; });
      set('[data-hf-pct]', (el) => { el.textContent = `-${v.pct}%`; el.hidden = !v.pct; });
      set('[data-hf-offer-text]', (el) => (el.textContent = v.pct ? el.dataset.tpl.replace('[saving]', v.saving) : el.dataset.full));
      if (cart) { cart.disabled = !v.available; cart.textContent = v.available ? cart.dataset.label : cart.dataset.sold; }
      set('[data-hf-buy]', (b) => (b.disabled = !v.available));
      const url = new URL(location.href); url.searchParams.set('variant', v.id); history.replaceState(null, '', url);
      if (v.media >= 0) {
        const thumb = section.querySelector(`[data-hf-thumb="${v.media}"]`);
        if (thumb) thumb.click();
        else {
          const track = section.querySelector('[data-hf-gallery] [data-hf-track]');
          const slide = track && track.children[v.media];
          if (slide) track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: 'smooth' });
        }
      }
    };
    qa(root, '[data-hf-opt]').forEach((i) => i.addEventListener('change', update));
  }

  // Sun / Night switch: each button sets <html data-hf-theme>; the choice is remembered on this device.
  function syncModeButtons() {
    const mode = document.documentElement.getAttribute('data-hf-theme') === 'dark' ? 'dark' : 'light';
    qa(document, '[data-hf-mode-set]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.hfModeSet === mode)));
  }
  function initModeButton(btn) {
    if (btn.__hfMode) return;
    btn.__hfMode = true;
    btn.addEventListener('click', () => {
      const mode = btn.dataset.hfModeSet === 'dark' ? 'dark' : 'light';
      document.documentElement.setAttribute('data-hf-theme', mode);
      try { localStorage.setItem('hf-theme', mode); } catch (e) {}
      syncModeButtons();
    });
  }

  function init(scope = document) {
    qa(scope, '[data-hf-mode-set]').forEach(initModeButton);
    syncModeButtons();
    qa(scope, '[data-hf-track-root]').forEach(initTrack);
    qa(scope, '[data-hf-tabs-root]').forEach(initTabs);
    qa(scope, '[data-hf-hero]').forEach(initHero);
    qa(scope, '[data-hf-gallery]').forEach(initGallery);
    qa(scope, '[data-hf-variants-root]').forEach(initVariants);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
